'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
  onScrollToId?: (id: string) => void;
}

export default function Slide25AmbassadorBenefits({ id, registerRef, onScrollToId }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#f5f0e8]">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1689359319646-4f8b0dbd02b0"
          alt="Rừng xanh thiên nhiên Việt Nam với ánh sáng lọc qua tán cây"
          className="w-full h-full object-cover opacity-6"
        />
        <div className="absolute inset-0 bg-[#f5f0e8]/93" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-16 min-h-screen flex flex-col justify-center">
        {/* Header */}
        <div className="fade-in-up mb-8 text-center">
          <p className="font-display font-600 text-[clamp(11px,1.4vw,14px)] text-[#4caf7d] uppercase tracking-[0.15em] mb-2">
            Student Ambassador
          </p>
          <h2 className="font-display font-800 text-[clamp(22px,3.5vw,44px)] leading-[1.1] text-white tracking-tight">
            Chính sách ưu đãi đề xuất
          </h2>
          <div className="w-[60px] h-[3px] bg-gradient-to-r from-[#4caf7d] to-[#a8d87a] rounded-full mx-auto mt-4" />
        </div>

        <div className="fade-in-up mb-6">
          <p className="font-body text-[14px] text-white leading-relaxed max-w-3xl mx-auto text-center">
            Đối với Student Ambassador, Tri An Green Travel có thể áp dụng hai hình thức quyền lợi riêng, tùy theo từng giai đoạn và từng nhóm sản phẩm, dịch vụ cụ thể trong hệ sinh thái.
          </p>
        </div>

        <div className="space-y-5 fade-in-up">
          {/* Card 1: Sử dụng trực tiếp */}
          <div className="rounded-2xl bg-[#1a4a2a] p-7">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[rgba(168,216,122,0.2)] border border-[rgba(168,216,122,0.3)] flex items-center justify-center flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a8d87a" strokeWidth="2"><path d="M20 12V22H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
              </div>
              <div className="flex-1">
                <h3 className="font-display font-700 text-[17px] text-white mb-2">
                  Quyền lợi khi Ambassador trực tiếp sử dụng
                </h3>
                <p className="font-body text-[13px] text-white/85 leading-relaxed mb-3">
                  Khi Student Ambassador sử dụng một số sản phẩm hoặc dịch vụ trong hệ sinh thái Trị An Holding, các bạn có thể được áp dụng mức ưu đãi từ <strong className="text-[#a8d87a]">30% đến 50%</strong> theo chính sách của từng thời điểm.
                </p>
                <div className="rounded-xl bg-[rgba(168,216,122,0.1)] border border-[rgba(168,216,122,0.2)] px-5 py-3 inline-flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a8d87a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                  <span className="font-body text-[12px] text-white/75">
                    Ví dụ: nếu giá 1 đêm ở Đảo là 1.000.000 VNĐ, Ambassador có thể được hưởng mức giá ưu đãi tương ứng theo chương trình đang áp dụng.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Giới thiệu thành công */}
          <div className="rounded-2xl bg-[#1a4a2a] p-7">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[rgba(232,160,48,0.2)] border border-[rgba(232,160,48,0.3)] flex items-center justify-center flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e8a030" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div className="flex-1">
                <h3 className="font-display font-700 text-[17px] text-white mb-2">
                  Quyền lợi khi Ambassador giới thiệu thành công
                </h3>
                <p className="font-body text-[13px] text-white/85 leading-relaxed mb-3">
                  Ngoài quyền lợi sử dụng trực tiếp, Student Ambassador còn có thể được ghi nhận khi giới thiệu thành công khách hàng hoặc người dùng phù hợp. Tri An Green Travel có thể áp dụng mức ghi nhận tương đương <strong className="text-[#e8a030]">15% giá trị đơn hàng</strong> hoặc giá trị quy đổi.
                </p>
                <p className="font-body text-[12px] text-white/70 mb-3">Phần ghi nhận này có thể được nhận dưới các hình thức như:</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['tiền mặt', 'credit hệ sinh thái Trị An Holding', 'vật phẩm hoặc hiện vật có giá trị tương đương'].map((item, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full bg-[rgba(232,160,48,0.15)] border border-[rgba(232,160,48,0.25)] font-body text-[12px] text-[#e8a030]">
                      {item}
                    </span>
                  ))}
                </div>
                <div className="rounded-xl bg-[rgba(232,160,48,0.1)] border border-[rgba(232,160,48,0.2)] px-5 py-3 inline-flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e8a030" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                  <span className="font-body text-[12px] text-white/75">
                    Ví dụ: nếu sản phẩm/dịch vụ có giá trị 1.000.000 VNĐ, Ambassador có thể được ghi nhận <strong className="text-[#e8a030]">150.000 VNĐ</strong> theo hình thức phù hợp.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {onScrollToId && (
          <div className="fade-in-up text-center mt-6">
            <button
              onClick={() => onScrollToId('slide-25b')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1a4a2a] text-white font-display font-600 text-[13px] hover:bg-[#2a6a3a] transition-colors"
            >
              Xem thêm — Trách nhiệm của Student Ambassador
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-[rgba(26,74,42,0.15)] flex flex-col sm:flex-row items-center justify-center gap-4 fade-in-up">
          <span className="font-body text-[11px] text-[#3a5a3a]/70 uppercase tracking-wider">HOTLINE: 0827092079</span>
          <span className="hidden sm:block text-[#3a5a3a]/30">|</span>
          <span className="font-body text-[11px] text-[#3a5a3a]/70 uppercase tracking-wider">EMAIL: triangreentravel@gmail.com</span>
        </div>
      </div>
    </SlideWrapper>
  );
}
